import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { ContractCgrpOff } from '@core/models/contract-cgrp-off.model';
import { ContractItem } from '@core/models/contract-item.model';
import { ContractNatioNego } from '@core/models/contract-natio-nego.model';
import { Contract } from '@core/models/contract.model';
import { ContractCgrpOffService } from '@core/services/contract-cgrp-off/contract-cgrp-off.service';
import { ContractService } from '@core/services/contract/contract.service';
import { merge, Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { CGRPOffManagingDialog } from './cgrp-off-managing-dialog/cgrp-off-managing-dialog.component';

@Component({
  selector: 'app-contract-cgrp-off',
  templateUrl: './contract-cgrp-off.component.html',
  styleUrls: ['./contract-cgrp-off.component.scss'],
  standalone: false,
})
export class ContractCgrpOffComponent implements OnInit, OnDestroy {
  @Input() contract: Contract;
  @Input() contractItem: ContractItem;
  @Input() natioNegotiationList: ContractNatioNego[];
  itemsFilterForm: FormGroup;
  componentDestroyed$: Subject<void> = new Subject();
  panelOpened = false;
  isContractOffLoading = false;
  isSavingCgrpOff = false;
  contractOffList: ContractCgrpOff[] = [];
  contractOffCards: { line: ContractCgrpOff; categoryCount: number }[] = [];
  readonly conditionColumns = ['category', 'name', 'number'];
  searchFormControl = new FormControl(null);
  filter = {};
  public cgrpOffOptions = [
    { label: 'SPORT', control: new FormControl(false) },
    { label: 'MAJORATION', control: new FormControl(false) },
    { label: 'F2 13:21 (SD)', control: new FormControl(false) },
    { label: 'F2 19:51 (LàD)', control: new FormControl(false) },
    { label: 'F5 19:41 (LàD)', control: new FormControl(false) },
    { label: 'F5 19:51 (LàV)', control: new FormControl(false) },
  ];

  get isCgrpOffBusy(): boolean {
    return this.isSavingCgrpOff || this.isContractOffLoading;
  }

  constructor(
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    public contractCgrpOffService: ContractCgrpOffService,
    private contractService: ContractService,
    private fb: FormBuilder,
    public dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {
    this.itemsFilterForm = this.fb.group({
      startDate: [null],
      endDate: [null],
      search: [null],
    });

    iconRegistry.addSvgIcon('content-copy', sanitizer.bypassSecurityTrustResourceUrl('assets/images/icons/content-copy.svg'));
    iconRegistry.addSvgIcon('edit', sanitizer.bypassSecurityTrustResourceUrl('assets/images/icons/edit.svg'));
    iconRegistry.addSvgIcon('delete-bin', sanitizer.bypassSecurityTrustResourceUrl('assets/images/icons/delete-bin.svg'));
    iconRegistry.addSvgIcon('add-circle-fill', sanitizer.bypassSecurityTrustResourceUrl('assets/images/icons/add-circle-fill.svg'));
    iconRegistry.addSvgIcon('delete-circle-fill', sanitizer.bypassSecurityTrustResourceUrl('assets/images/icons/delete-circle-fill.svg'));
  }

  ngOnInit(): void {
    this.initForm();
    this.getContractOffList();
    if (this.contractItem) {
      this.cgrpOffOptions[0].control.setValue(!!this.contractItem.sport);
      this.cgrpOffOptions[1].control.setValue(!!this.contractItem.majoration);
      this.cgrpOffOptions[2].control.setValue(!!this.contractItem.f2_13_21);
      this.cgrpOffOptions[3].control.setValue(!!this.contractItem.f2_19_51);
      this.cgrpOffOptions[4].control.setValue(!!this.contractItem.f5_19_41);
      this.cgrpOffOptions[5].control.setValue(!!this.contractItem.f5_19_51);
    }

    // Subscribe to changes in cgrpOffOptions controls
    this.cgrpOffOptions.forEach((option, index) => {
      option.control.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe(value => {
        this.contractItem.sport = this.cgrpOffOptions[0].control.value ? 1 : 0;
        this.contractItem.majoration = this.cgrpOffOptions[1].control.value ? 1 : 0;
        this.contractItem.f2_13_21 = this.cgrpOffOptions[2].control.value ? 1 : 0;
        this.contractItem.f2_19_51 = this.cgrpOffOptions[3].control.value ? 1 : 0;
        this.contractItem.f5_19_41 = this.cgrpOffOptions[4].control.value ? 1 : 0;
        this.contractItem.f5_19_51 = this.cgrpOffOptions[5].control.value ? 1 : 0;

        // Call API to save changes
        this.contractService
          .updateContractItem(this.contractItem)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(
            response => {},
            error => {
              // Revert the control value on error
              option.control.setValue(!value, { emitEvent: false });
            }
          );
      });
    });
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.unsubscribe();
  }

  public initForm(): void {
    this.itemsFilterForm.addControl('startDate', this.fb.control(null, [Validators.required]));
    this.itemsFilterForm.addControl('endDate', this.fb.control(null, [Validators.required]));
    this.itemsFilterForm.addControl('search', this.fb.control(null, [Validators.required]));
  }

  public searchContractOffList() {
    this.filter = {
      startDate: this.itemsFilterForm.get('startDate').value ?? '',
      endDate: this.itemsFilterForm.get('endDate').value ?? '',
      name: this.searchFormControl.value ?? '',
    };

    this.getContractOffList(false);
  }

  private getContractOffList(isReload = false) {
    this.isContractOffLoading = true;
    this.filter = Object.assign(this.filter, {
      clientId: this.contract ? this.contract.clientId : '',
      stateContractYear: this.contract ? this.contract.stateContractYear : '',
    });

    this.contractCgrpOffService
      .getList(this.filter)
      .pipe(
        take(1),
        takeUntil(this.componentDestroyed$),
        finalize(() => {
          this.isContractOffLoading = false;
          if (isReload) this.isSavingCgrpOff = false;
        })
      )
      .subscribe(
        offList => {
          this.contractOffList = offList.items;
          this.sortContractOffList();
        },
        error => {}
      );
  }

  public valueChange(): void {
    let todayDate = new Date();

    merge(this.itemsFilterForm.get('startDate').valueChanges, this.itemsFilterForm.get('endDate').valueChanges)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        () => {
          // TODO
        },
        (error: Error) => {
          console.error(error);
        }
      );
  }

  public editCGRPOffModal(cgrpOff = null, action = 'new'): void {
    const dialogRef = this.dialog.open(CGRPOffManagingDialog, {
      maxHeight: 'calc(100dvh - 32px)',
      // width: '1000px',
      autoFocus: false,
      data: {
        contract: this.contract,
        contractItem: this.contractItem,
        cgrpOffData: cgrpOff,
        action: action,
      },
      panelClass: 'tv-dialog-container',
    });

    dialogRef.componentInstance.saveStarted
      .pipe(takeUntil(dialogRef.afterClosed()), takeUntil(this.componentDestroyed$))
      .subscribe(() => (this.isSavingCgrpOff = true));

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(rs => {
        if (rs && rs['action'] === 'reload') {
          this.getContractOffList(true);
        }
      });
  }

  public dupCGRPOff(cgrpOff = null): void {
    const dialogRef = this.dialog.open(CGRPOffManagingDialog, {
      maxHeight: 'calc(100dvh - 32px)',
      autoFocus: false,
      data: {
        contract: this.contract,
        contractItem: this.contractItem,
        cgrpOffData: cgrpOff,
        action: 'dup',
      },
      panelClass: 'tv-dialog-container',
    });

    dialogRef.componentInstance.saveStarted
      .pipe(takeUntil(dialogRef.afterClosed()), takeUntil(this.componentDestroyed$))
      .subscribe(() => (this.isSavingCgrpOff = true));

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(rs => {
        if (rs && rs['action'] === 'reload') {
          this.getContractOffList(true);
        }
      });
  }

  public editCGRPOff(cgrpOff): void {
    const dialogRef = this.dialog.open(CGRPOffManagingDialog, {
      maxHeight: 'calc(100dvh - 32px)',
      autoFocus: false,
      width: '1000px',
      data: {
        contract: this.contract,
        contractItem: this.contractItem,
        cgrpOffData: cgrpOff,
        action: 'edit',
      },
      panelClass: 'tv-dialog-container',
    });

    dialogRef.componentInstance.saveStarted
      .pipe(takeUntil(dialogRef.afterClosed()), takeUntil(this.componentDestroyed$))
      .subscribe(() => (this.isSavingCgrpOff = true));

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(rs => {
        if (rs && rs['action'] === 'reload') {
          this.getContractOffList(true);
        }
      });
  }

  public confirmDelete(cgrpOff): void {
    const dialogRef = this.dialog.open(CGRPOffManagingDialog, {
      autoFocus: false,
      width: '450px',
      data: {
        contract: this.contract,
        contractItem: this.contractItem,
        cgrpOffData: cgrpOff,
        action: 'delete',
      },
      panelClass: 'tv-dialog-container',
    });

    dialogRef.afterClosed().subscribe(rs => {
      if (rs && rs['action'] === 'delete') {
        this.deleteCgrpOff(cgrpOff);
      }
    });
  }

  // functions for css animations
  onPanelOpened() {
    this.panelOpened = true;
  }

  onPanelClosed() {
    this.panelOpened = false;
  }

  public duplicateItem(item) {
    this.contractCgrpOffService.duplicate(item.id).subscribe(
      rs => {
        this.snackBar.open('La duplication a été validée.', null, {
          duration: 4000,
          verticalPosition: 'top',
          panelClass: ['tv-dialog-container'],
        });
        this.getContractOffList(true);
      },
      error => {
        this.snackBar.open('La duplication a été échouée.', null, {
          duration: 4000,
          verticalPosition: 'top',
          panelClass: ['tv-dialog-container'],
        });
      }
    );
  }

  public deleteCgrpOff(item) {
    this.contractCgrpOffService.delete(item.id).subscribe(
      rs => {
        this.snackBar.open('La suppression a été validée.', null, {
          duration: 4000,
          verticalPosition: 'top',
          panelClass: ['tv-dialog-container'],
        });
        this.getContractOffList(true);
      },
      error => {
        this.snackBar.open('La suppression a été échouée.', null, {
          duration: 4000,
          verticalPosition: 'top',
          panelClass: ['tv-dialog-container'],
        });
      }
    );
  }

  private sortContractOffList() {
    this.contractOffCards = this.contractOffList.map(line => {
      line.conditions = [...(line.conditions ?? [])].sort(
        (a, b) =>
          (a.categoryType?.name || '').localeCompare(b.categoryType?.name || '', 'fr') ||
          (a.category?.name || '').localeCompare(b.category?.name || '', 'fr')
      );
      return {
        line,
        categoryCount: new Set(line.conditions.map(condition => condition.categoryType?.id).filter(id => id != null)).size,
      };
    });
  }
}
